import { CheckCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError } from "@/lib/api";

/**
 * INS-7 — approve every pending insight of one source in one decision.
 *
 * **Only ever below the list it approves.** The bar is drawn after the last card of its source, and
 * what it sends is the ids of the cards that were actually drawn — so the Server can refuse (409)
 * when what is pending is not what was shown: a card added by a re-extraction, one discarded on
 * another screen, a list that never finished loading. The gate stays a human act; it simply stops
 * being thirteen of them for a meeting the owner has just read.
 *
 * Improve and discard stay on each card above, and that is the order the bar's sentence asks for:
 * fix or drop what is wrong, then approve what is left.
 */
export function ApproveSourceBar({
  sourceId,
  pendingIds,
  sourceName,
  onApproved,
  onStale,
  reviewLink = false,
}: {
  sourceId: string;
  /** Every pending insight of the source that is on screen, in the order drawn. */
  pendingIds: string[];
  /** How the sentence names the source — "Planning" — or null for "this source". */
  sourceName: string | null;
  onApproved: (count: number) => void;
  /** The Server said the list changed: reload it, so the owner reads what is really waiting, and
   * show the Server's sentence — a screen that handles this says it; the bar does not. */
  onStale?: (message: string) => void;
  /** Whether to point at the review queue for improving or discarding one card — for a screen
   * whose rows have no such controls of their own. */
  reviewLink?: boolean;
}) {
  const { api, refreshWorldViewActivity } = useApp();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const count = pendingIds.length;
  if (count === 0) return null;

  async function approveAll() {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const result = await api.approveSource(sourceId, pendingIds);
      if (result.synthesizing) refreshWorldViewActivity();
      onApproved(result.approved);
    } catch (cause: unknown) {
      if (cause instanceof ApiError && cause.status === 409 && onStale) {
        // Said by the screen that reloads the list, not here: the reload can move this bar under
        // a card that was not there before, which would remount it and lose the sentence.
        onStale(cause.message);
      } else {
        setProblem(cause instanceof ApiError ? cause.message : "That could not be saved. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  const noun = count === 1 ? "insight" : "insights";

  return (
    <div
      className="flex flex-wrap items-center gap-[var(--space-3)] rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-3)]"
      data-testid="approve-source"
    >
      <p className="min-w-[240px] flex-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        {count === 1 ? "That is the one" : `Those are all ${count}`} pending {noun} from{" "}
        <strong className="font-semibold text-[var(--text-primary)]">{sourceName ?? "this source"}</strong>.{" "}
        {reviewLink ? (
          <>
            To improve or discard one first, use the{" "}
            <Link to="/review" className="underline underline-offset-2 hover:text-[var(--text-primary)]">
              review queue
            </Link>
            .
          </>
        ) : (
          "Improve or discard any of them first."
        )}
      </p>

      <button
        type="button"
        disabled={busy}
        onClick={() => void approveAll()}
        className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-4)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
      >
        <CheckCheck size={14} strokeWidth={2.5} />
        {busy ? "Approving…" : count === 1 ? "Approve it" : `Approve all ${count}`}
      </button>

      {problem && (
        <p role="alert" className="basis-full text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}
    </div>
  );
}
